import React, { memo } from 'react';

import styled from 'styled-components';

import { getLayoutStyle } from 'styles/components/getLayoutStyle';

import InfoButton from './InfoButton';

/**
 * @desc 정보 버튼 그룹 컴포넌트 (기본형)
 * @param {Array} button - 버튼 데이터 배열
 * @param {string} className - 추가적인 클래스명
 * @param {string} info - 그룹 공통 툴팁 문구 (첫 번째 버튼에 적용)
 * @param {boolean} visible - 툴팁 초기 가시성
 * @param {string} arrow - 툴팁 화살표 방향
 * @param {boolean} isOutside - 외부 클릭 시 닫힘 여부
 * @param {string} layout - 레이아웃 타입
 */
const InfoButtonGroup = ({
  button,
  className,
  info: groupInfo,
  visible: groupVisible,
  arrow: groupArrow,
  isOutside: groupIsOutside,
  layout = 'column',
}) => {
  const [isTooltipClosed, setIsTooltipClosed] = React.useState(false);

  const handleClose = (btnOnClose) => {
    setIsTooltipClosed(true);
    btnOnClose && btnOnClose();
  };

  return (
    <InfoButtonGroupStyle className={className} layout={layout}>
      {button.map(
        (
          {
            text,
            icon,
            beforeIcon,
            disabled,
            info,
            arrow,
            visible,
            onClick,
            onClose,
            className: buttonClassName,
            isClose,
            isOutside,
            shape,
            variant,
            size,
          },
          idx
        ) => {
          const isFirst = idx === 0;
          const currentInfo = isFirst ? (groupInfo ?? info) : info;
          const noInfoClass = !currentInfo ? ' no_info' : '';

          // 첫 번째 버튼의 툴팁 가시성 결정 (사용자가 직접 닫았으면 false, 아니면 prop에 따름)
          const currentVisible = isFirst
            ? isTooltipClosed
              ? false
              : groupVisible ?? visible
            : visible ?? false;

          return (
            <InfoButton
              icon={icon}
              key={idx}
              className={(buttonClassName || '') + noInfoClass}
              disabled={disabled}
              beforeIcon={beforeIcon}
              info={currentInfo}
              arrow={isFirst ? (groupArrow ?? arrow) : arrow}
              visible={currentVisible}
              onClick={onClick}
              onClose={() => handleClose(onClose)}
              isClose={isClose}
              isOutside={isFirst ? (groupIsOutside ?? isOutside) : isOutside}
              shape={shape}
              variant={variant}
              size={size}
              disableClickToggle={true}
            >
              {text}
            </InfoButton>
          );
        }
      )}
    </InfoButtonGroupStyle>
  );
};

const InfoButtonGroupStyle = styled.div`
  display: flex;
  flex-direction: ${({ layout }) => (layout === 'column' ? 'column' : 'row')};

  ${({ layout }) => getLayoutStyle(layout)}

  .no_info {
    .info_button_box {
      display: none !important;
    }
  }
  .info_button_box {
    left: 0;
    transform: none;
    bottom: 120%;
    border-radius: 0;

    &::before {
      left: 25px;
      transform: translateX(-50%);
      border-top-color: ${({ theme }) => theme.color.gray200};
    }
    &::after {
      left: 25px;
      transform: translateX(-50%);
      border-top-color: ${({ theme }) => theme.color.white};
    }
  }
  .info_content {
    font-weight: 400;
    white-space: nowrap;
    min-width: auto;
  }
`;

/**
 * @desc 탭 전용 정보 버튼 그룹 스타일
 */
const TabInfoButtonGroup = styled(InfoButtonGroup)`
  .mtr_info_btn {
    border-radius: 0;
  }
`;

/**
 * @desc 언어 선택 등 맵 액션 전용 정보 버튼 그룹 스타일
 */
const MapActionInfoButtonGroup = styled(InfoButtonGroup)`
  gap: 8px;
  button {
    width: auto !important;
    padding: 0px 8px !important;
    border: 1px solid transparent;
    border-radius: 6px;
    display: block;
  }
  .info_button_box {
    bottom: 130% !important;
  }
`;

const MemoizedInfoButtonGroup = memo(InfoButtonGroup);
MemoizedInfoButtonGroup.Tab = TabInfoButtonGroup;
MemoizedInfoButtonGroup.MapAction = MapActionInfoButtonGroup;

export default MemoizedInfoButtonGroup;
